<script>
  import { onMount, onDestroy } from 'svelte'
  import SetRating from './SetRating.svelte'
  import Field from './Field.svelte'
  import Multiline from './Multiline.svelte'
  import FormHeader from './FormHeader.svelte'
  import DropZone from './DropZone.svelte'
  import ImagePreviewList from './ImagePreviewList.svelte'
  import Button from './Button.svelte'
  import CheckBox from './CheckBox.svelte'
  import BackButton from './BackButton.svelte'
  import SuccessModal from './Success.svelte'
  import ErrorModal from './Error.svelte'
  import { validateFields } from '../validation'
  import { isFileSizeAboveOneMegabyte } from '../utils'
  import {
    submitReview,
    getSignedUrlsForSelectedFiles,
    uploadFilesToGoogleCloudStorage,
    saveMediaData
  } from '../api'
  import loggerMod from '../loader/log'

  import configMod from '../config'
  const config = configMod()
  const log = loggerMod()

  export let settings
  export let close
  export let productId
  export let widgetType

  let userRating = 5
  const email = {
    value: '',
    error: false
  }
  const name = {
    value: '',
    error: false
  }
  const review = {
    value: '',
    error: false
  }
  const gdpr = {
    value: false,
    error: false
  }

  let loading = false
  let hasError = false
  let hasSuccess = false
  let requiredText
  let emailMessage
  let primaryColor
  let successMessage
  let addReview
  let submitReviewText
  let emailPlaceholder
  let namePlaceholder
  let reviewPlaceholder
  let starColor
  let reviewLengthError
  let selectedFiles = []
  let fileError = false
  let mediaUrls = []
  let showGDRPBanner
  let submittedReview
  let labelColor
  let shareToText
  const businessId = config.aid
  const clientId = config.apiKey

  $: mediaUrls = selectedFiles.map(file => URL.createObjectURL(file))
  onMount(() => {
    primaryColor = settings.widget_settings.general_settings.primary_color
    addReview = settings.translations.review_form.write_review_text
    submitReviewText = settings.translations.review_form.submit_button_text
    successMessage = settings.apm.translations.thank_you_for_the_review_text
    shareToText = settings.apm.translations.share_to_text
    emailMessage = settings.translations.review_form.invalid_email_text
    requiredText = {
      email: settings.translations.review_form.email_required_text,
      name: settings.translations.review_form.name_required_text,
      review: settings.translations.review_form.review_content_required_text,
      gdpr: settings.translations.review_form.gdpr_required_text
    }

    emailPlaceholder = settings.translations.review_form.email_placeholder
    reviewPlaceholder = settings.translations.review_form.message_placeholder
    namePlaceholder = settings.translations.review_form.name_placeholder
    starColor = settings.widget_settings.general_settings.star_rating_color
    reviewLengthError = settings.translations.review_length_text
    showGDRPBanner = settings.widget_settings.review_form.show_gdrp_banner
    labelColor = settings.widget_settings.general_settings.form_label_text_color
  })

  onDestroy(() => {
    mediaUrls = mediaUrls.map(mediaUrl => URL.revokeObjectURL(mediaUrl))
  })

  const navigateBack = () => {
    close()
  }

  const uploadSelectedFiles = async () => {
    try {
      const signedUrls = await getSignedUrlsForSelectedFiles(
        clientId,
        selectedFiles
      )
      const uploadedFiles = await uploadFilesToGoogleCloudStorage(signedUrls)
      const media = await saveMediaData(uploadedFiles, clientId, businessId)
      return media
    } catch (error) {
      fileError = 'Failed to upload files'
    }
  }

  const handleSubmitReview = async () => {
    loading = true
    if (formValid()) {
      try {
        const savedMedia = await uploadSelectedFiles()

        const reviewData = {
          business_id: businessId,
          platform: 'kudobuzz',
          source: 'widget',
          message: review.value,
          rating: userRating,
          ...(productId && { external_unique_id: productId }),
          reviewer: {
            display_name: name.value,
            email: email.value,
            channel: 'email'
          },
          ...(savedMedia &&
            savedMedia.length > 0 && {
            media: savedMedia.map(({ data }) => data.id)
          })
        }

        const response = await submitReview(reviewData, clientId)
        if (response && response.error) {
          hasError = true
        } else {
          submittedReview = response.data
          hasSuccess = true
        }
      } catch (error) {
        log.error(error)
        hasError = true
      }
    }
    loading = false
  }

  const formValid = () => {
    const emailError = validateFields.validateEmail(
      email.value,
      requiredText.email,
      emailMessage
    )
    const nameError = validateFields.validateLength(
      name.value,
      requiredText.name
    )
    const reviewError = validateFields.validateReviewLength(
      review.value,
      4,
      requiredText.review,
      reviewLengthError
    )

    email.error = emailError
    name.error = nameError
    review.error = reviewError
    gdpr.error = showGDRPBanner && !gdpr.value && requiredText.gdpr

    if (
      email.error === false &&
      name.error === false &&
      review.error === false &&
      gdpr.error === false
    ) {
      return true
    }
    return false
  }

  const onSelectMedia = event => {
    const files = []
    event.target.files &&
      [...event.target.files].forEach(file => {
        if (isFileSizeAboveOneMegabyte(file)) {
          fileError =
            settings.translations.review_form.maximum_image_and_video_size_text
        } else {
          files.push(file)
        }
      })

    selectedFiles = [...selectedFiles, ...files]
  }

  const handleRemoveImage = selectedIndex => {
    selectedFiles = selectedFiles.filter((_, index) => index !== selectedIndex)
  }

  function handleUserRating (event) {
    userRating = event.detail.rating
  }
</script>

<style>
  :global(.wrapper__add-review-2b4eN5) {
    display: flex;
    min-height: 600px;
    flex-direction: column;
    padding: 0 20px 20px 20px;
    width: 100%;
    box-sizing: border-box;
    position: relative;
  }

  :global(.kb-form-1760Is) {
    display: flex;
    flex-direction: column;
    width: 100%;
    flex: 1;
  }

  :global(.kb-form-content-N572nm) {
    overflow-y: auto;
  }

  @media only screen and (max-height: 670px) {
    :global(.kb-form-content-N572nm) {
      height: 80vh;
    }
  }

  :global(.rating-2WhK_p) {
    margin-bottom: 24px;
  }

  :global(.button__submit-review-2q6bGO) {
    width: 100%;
    margin-top: auto;
  }
</style>

<div class="wrapper__add-review-2b4eN5">
  {#if hasSuccess}
    <SuccessModal
      review={submittedReview}
      message={successMessage}
      {shareToText}
      shopShareUrl={settings.domain}
      shopShareLogo={settings.logo}
      {navigateBack}
      buttonColor={primaryColor}
      {widgetType} />
  {:else if hasError}
    <ErrorModal />
  {:else}
    <FormHeader title={addReview}>
      {#if widgetType === 'neptune_widget'}
        <BackButton {navigateBack} />
      {/if}
    </FormHeader>

    <div class="kb-form-content-N572nm">
      <div class="kb-form-1760Is">
        <div class="rating-2WhK_p">
          <h4 style="color: {labelColor}; margin: 21px 0;">
            {settings.translations.review_form.review_rating_text}
          </h4>
          <div class="">
            <SetRating {starColor} on:setRating={handleUserRating} />
          </div>
        </div>

        <Multiline
          placeholder={reviewPlaceholder}
          bind:value={review.value}
          error={review.error}
          height={184} />

        <div>
          <DropZone error={fileError} dragAndDrop={onSelectMedia} {settings}>
            {#if mediaUrls.length}
              <ImagePreviewList {mediaUrls} {handleRemoveImage} />
            {/if}
          </DropZone>
        </div>

        <Field
          placeholder={emailPlaceholder}
          bind:value={email.value}
          error={email.error} />

        <Field
          placeholder={namePlaceholder}
          bind:value={name.value}
          error={name.error} />

        {#if showGDRPBanner}
          <CheckBox
            label={settings.translations.review_form.gdrp_text}
            style="color: {labelColor};"
            bind:checked={gdpr.value}
            error={gdpr.error} />
        {/if}
        <div class="button__submit-review-2q6bGO">
          <Button
            hasIcon={false}
            name="submit-review"
            on:buttonClicked={handleSubmitReview}
            backgroundColor={primaryColor}
            disabled={loading}
            buttonText={submitReviewText} />
        </div>
      </div>
    </div>
  {/if}
</div>
